import { NUMERIC_AGGREGATION, type NumericAggregationCode } from './numeric-aggregation.codes';

export { NUMERIC_AGGREGATION, type NumericAggregationCode };

/** Máximo de métricas numéricas configuráveis por etapa (board + lista). A contagem de cards é sempre no header. */
export const MAX_BOARD_STAGE_METRICS = 3;

export type BoardStageMetricEntry =
  /** Legado — normalizado para `numeric` com aggregation 1. */
  | { kind: 'sum'; fieldId: string }
  | { kind: 'numeric'; fieldId: string; aggregation: NumericAggregationCode };

export interface BoardStageMetricConfig {
  metrics: BoardStageMetricEntry[];
}

/** Formato legado v1 (localStorage) — migrado ao carregar. */
export type BoardStageMetricMode = 'none' | 'count' | 'sum';

export interface BoardStageMetricConfigV1 {
  mode: BoardStageMetricMode;
  sumFieldId?: string;
}

function toNumericAggregationCode(n: number): NumericAggregationCode {
  if (n === NUMERIC_AGGREGATION.Sum) return NUMERIC_AGGREGATION.Sum;
  if (n === NUMERIC_AGGREGATION.Average) return NUMERIC_AGGREGATION.Average;
  if (n === NUMERIC_AGGREGATION.Min) return NUMERIC_AGGREGATION.Min;
  if (n === NUMERIC_AGGREGATION.Max) return NUMERIC_AGGREGATION.Max;
  if (n === NUMERIC_AGGREGATION.Product) return NUMERIC_AGGREGATION.Product;
  if (n === NUMERIC_AGGREGATION.SubtractOldestMinusRest) return NUMERIC_AGGREGATION.SubtractOldestMinusRest;
  if (n === NUMERIC_AGGREGATION.DivideOldestByProductRest) return NUMERIC_AGGREGATION.DivideOldestByProductRest;
  return NUMERIC_AGGREGATION.Sum;
}

export function migrateBoardStageMetricConfig(raw: unknown): BoardStageMetricConfig {
  if (raw && typeof raw === 'object' && Array.isArray((raw as BoardStageMetricConfig).metrics)) {
    return normalizeBoardStageMetricConfig(raw as BoardStageMetricConfig);
  }
  const v1 = raw as BoardStageMetricConfigV1 | null;
  if (!v1 || typeof v1 !== 'object') return { metrics: [] };
  if (v1.mode === 'none') return { metrics: [] };
  if (v1.mode === 'count') return { metrics: [] };
  if (v1.mode === 'sum' && v1.sumFieldId?.trim()) {
    return {
      metrics: [
        {
          kind: 'numeric',
          fieldId: v1.sumFieldId.trim(),
          aggregation: NUMERIC_AGGREGATION.Sum,
        },
      ],
    };
  }
  return { metrics: [] };
}

export function normalizeBoardStageMetricConfig(config: BoardStageMetricConfig): BoardStageMetricConfig {
  const seen = new Set<string>();
  const metrics: BoardStageMetricEntry[] = [];

  for (const e of config.metrics ?? []) {
    if (metrics.length >= MAX_BOARD_STAGE_METRICS) break;

    // Legado / localStorage: "contagem de cards" não é métrica configurável — sempre no header.
    if ((e as { kind?: string }).kind === 'card_count') continue;

    if (e.kind === 'sum') {
      const id = e.fieldId?.trim();
      if (!id) continue;
      const key = `${id}:${NUMERIC_AGGREGATION.Sum}`;
      if (seen.has(key)) continue;
      seen.add(key);
      metrics.push({
        kind: 'numeric',
        fieldId: id,
        aggregation: NUMERIC_AGGREGATION.Sum,
      });
      continue;
    }

    if (e.kind === 'numeric') {
      const id = e.fieldId?.trim();
      if (!id) continue;
      const agg = toNumericAggregationCode(e.aggregation);
      const key = `${id}:${agg}`;
      if (seen.has(key)) continue;
      seen.add(key);
      metrics.push({ kind: 'numeric', fieldId: id, aggregation: agg });
    }
  }
  return { metrics };
}

export function parseStoredStageMetricMap(raw: unknown): Record<string, BoardStageMetricConfig> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, BoardStageMetricConfig> = {};
  for (const [stageId, value] of Object.entries(raw as Record<string, unknown>)) {
    out[stageId] = migrateBoardStageMetricConfig(value);
  }
  return out;
}

/** Chip calculado para exibição (board / lista) — só campos numéricos; contagem de cards fica no título da coluna. */
export type BoardMetricDisplayChip = {
  kind: 'numeric';
  label: string;
  aggregation: NumericAggregationCode;
  value: number | null;
  formatCurrency: boolean;
  /**
   * Casas decimais do campo de origem (NUMBER: `numberDecimalPlaces`;
   * FORMULA: `formulaResultDecimalPlaces`; CURRENCY/PROGRESS/RATING: tratado pelo formatador).
   * Undefined → o formatador usa um fallback razoável.
   */
  decimalPlaces?: number;
};
